
import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useDevice from "../hooks/useDevice";
import Flex from "./Flex";
import Text from "./Text";
import Popper from "./Popper";
import Bubble from "./Bubble";

type GridInfoProps = {
    title: string;
    content: string;
    width?: number;
};

function GridInfo({ title, content, width = 200 }: GridInfoProps) {
    const popperRef = useRef<HTMLDivElement | null>(null);
    const bubbleRef = useRef<HTMLDivElement | null>(null);
    const [isPopperOpen, setIsPopperOpen] = useState(false);
    const device = useDevice();
    const isMobile = device !== "desktop";

    // 외부 클릭 닫기
    useEffect(() => {
        if (!isPopperOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;

            if (popperRef.current?.contains(target)) return;
            if (bubbleRef.current?.contains(target)) return;

            setIsPopperOpen(false);
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [isPopperOpen]);

    return (
        <Flex gap={4} alignItems="center">
            <Text weight="bold">{title}</Text>

            <div
                ref={popperRef}
                onClick={() => setIsPopperOpen((v) => !v)}
            >
                <img
                    src="/images/ico_info_gray.png"
                    alt="info"
                    style={{ cursor: "pointer" }}
                />
            </div>

            <Popper
                targetRef={popperRef}
                isOpen={true}
                closeOnOutsideClick={false}
                closeOnEscape={false}
            >
                <AnimatePresence mode="wait">
                    {isPopperOpen && (
                        <motion.div
                            key="grid-info-bubble"
                            ref={bubbleRef}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Bubble
                                padding={16}
                                width={isMobile ? 250 : width}
                                backgroundColor="#40434C"
                                border={0}
                                tailPosition="top-left"
                                closeColor="white"
                                style={{ position: "relative", left: -15 }}
                                onClose={() => setIsPopperOpen(false)}
                            >
                                <motion.div
                                    initial={{
                                        clipPath: "inset(0 100% 100% 0)",
                                    }}
                                    animate={{
                                        clipPath: "inset(0 0 0 0)",
                                    }}
                                    exit={{
                                        clipPath: "inset(0 100% 100% 0)",
                                    }}
                                    transition={{
                                        duration: 0.25,
                                        ease: "easeInOut",
                                    }}
                                    style={{ overflow: "hidden" }}
                                >
                                    <Text
                                        size={14}
                                        color="white"
                                        noWrap={false}
                                        style={{
                                            lineHeight: "1.3",
                                            userSelect: "none",
                                        }}
                                    >
                                        {content}
                                    </Text>
                                </motion.div>
                            </Bubble>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Popper>
        </Flex>
    );
}

export default GridInfo;
